import { useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { ArrowLeft, Globe, Clock, Plus } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { format } from "date-fns"
import { Skeleton } from "@/components/ui/skeleton"
import { EditServiceDialog } from "@/components/services/EditServiceDialog"
import { AddMediaDialog } from "@/components/services/AddMediaDialog"
import { useGetServiceById } from "@/services/query/services.query"
import { useToast } from "@/components/ui/use-toast"
import MediaUploadCard from "./components/MediaUploadCard"

function ServiceDetailsPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isMediaDialogOpen, setIsMediaDialogOpen] = useState(false)

  const {
    data: service,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetServiceById(parseInt(id))

  if (!id) {
    return (
      <div className="container mx-auto py-6">
        <div className="text-center text-red-500">Invalid service ID</div>
      </div>
    )
  }

  if (isLoading || !service) {
    return (
      <div className="container mx-auto py-6">
        <Button
          variant="ghost"
          className="mb-6 hover:bg-blue-50"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Services
        </Button>
        <ServiceDetailsSkeleton />
      </div>
    )
  }

  if (isError) {
    toast({
      title: "Error",
      description: error?.message || "Failed to load service details",
      variant: "destructive",
    })
    return (
      <div className="container mx-auto py-6">
        <div className="text-center text-red-500">
          Error loading service details
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-2">
        <Button
          variant="ghost"
          className="hover:bg-blue-50 w-fit mb-6"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Services
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-6 gap-4 sm:gap-6 lg:gap-8 lg:min-h-[calc(100vh-8rem)]">
          {/* Profile Section */}
          <Card className="lg:col-span-2 lg:h-[calc(100vh-8rem)] overflow-auto">
            <CardHeader className="sticky top-0 bg-white dark:bg-gray-900 z-10 pb-4 border-b">
              <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg">
                <span className="text-2xl sm:text-3xl font-bold text-white">
                  {service.title
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .slice(0, 2)}
                </span>
              </div>
              <CardTitle className="text-center text-xl sm:text-2xl">
                {service.title}
              </CardTitle>
              <CardDescription className="text-center">
                {service.meta?.href && (
                  <Badge variant="secondary" className="mt-2">
                    <a
                      href={service.meta.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center"
                    >
                      <Globe className="h-3 w-3 mr-1" />
                      External Link
                    </a>
                  </Badge>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center text-sm sm:text-base">
                <Clock className="h-4 w-4 mr-2 text-purple-500" />
                <span>
                  Last updated: {format(new Date(service.modified_at), "PPP")}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Tabs Section */}
          <div className="lg:col-span-4 lg:h-[calc(100vh-8rem)] flex flex-col">
            <Tabs
              defaultValue="details"
              className="flex-1 flex flex-col h-full"
            >
              <TabsList className="w-full grid grid-cols-1 px-1">
                <TabsTrigger value="details" className="text-sm sm:text-base">
                  Details
                </TabsTrigger>
              </TabsList>

              <TabsContent value="details" className="flex-1 mt-0 h-full">
                <Card className="h-full flex flex-col">
                  <CardHeader className="border-b shrink-0">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                      <div>
                        <CardTitle className="text-lg sm:text-xl lg:text-2xl">
                          Service Details
                        </CardTitle>
                        <CardDescription className="text-sm sm:text-base">
                          View and manage service information
                        </CardDescription>
                      </div>
                      <Button
                        onClick={() => setIsEditDialogOpen(true)}
                        className="w-full sm:w-auto shrink-0"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Edit Details
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="flex-1 overflow-hidden p-0">
                    <ScrollArea className="h-full">
                      <div className="p-6">
                        <div className="space-y-6">
                          {service.description &&
                            Object.entries(service.description).map(
                              ([key, value]) => (
                                <div key={key} className="space-y-2">
                                  <h3 className="text-lg font-semibold text-purple-600">
                                    {key}
                                  </h3>
                                  <p className="text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
                                    {value}
                                  </p>
                                </div>
                              )
                            )}
                        </div>
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      <EditServiceDialog
        service={service}
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        onSaved={refetch} // Use refetch instead of manual fetchServiceDetails
      />
      <AddMediaDialog
        serviceId={parseInt(id)}
        open={isMediaDialogOpen}
        onOpenChange={setIsMediaDialogOpen}
        onSuccess={refetch} // Use refetch instead of manual fetchServiceDetails
      />
    </div>
  )
}

function ServiceDetailsSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
      <div className="md:col-span-1">
        <Skeleton className="h-[300px] w-full rounded-lg" />
      </div>
      <div className="md:col-span-3">
        <Skeleton className="h-[500px] w-full rounded-lg" />
      </div>
    </div>
  )
}

export default ServiceDetailsPage
